import React, { useMemo } from 'react'
import {
  contains,
  getRelatedTarget,
  mergeClassnames,
  runIfFn,
  useCallbackRef,
  useEventListener,
  useOnScrollOutside
} from '@mote/helpers'
import { isMobile, preventBubblingEvent } from '@mote/helpers/browser'
import { KeyboardKey } from '@mote/helpers/constants'
import { DropdownDivider, DropdownHeader } from './dropdown.components'

export const HOVER_TRANSITION_DURATION = 100
export const CLICK_TRANSITION_DURATION = 150
export const HOVER_DELAY_DURATION = 100
export const TRIGGER = {
  click: 'click',
  hover: 'hover'
}

export const useDropdown = props => {
  const {
    id,
    onAction: _onAction,
    children: childrenProp,
    menuClassName,
    triggerClassName,
    finalFocusRef,
    trigger = TRIGGER.click,
    transitionType = 'scale',
    activeItemIndex = -1,
    selectedItemIndex = -1,
    isSelectable = false,
    returnFocusOnClose = true,
    focusItemOnHover = false,
    focusItemOnOpen = true,
    closeOnSelect = true,
    closeOnBlur = true,
    inline = false,
    disabled = false,
    defaultIsOpen = false,
    width = 215,
    variant = 'light',
    orientation = 'vertical',
    placement = 'bottom-right',
    'data-trigger-testid': triggerTestId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-controls': ariaControls,
    ...rest
  } = props

  const [isOpen, setIsOpen] = React.useState(defaultIsOpen)
  const activeItemIndexRef = React.useRef(activeItemIndex)
  const selectedItemIndexRef = React.useRef(selectedItemIndex)
  const menuItems = React.useRef([])
  const menuRef = React.useRef(null)
  const triggerRef = React.useRef(null)
  const isHoveringRef = React.useRef(false)
  const timeoutIds = React.useRef({
    trigger: null,
    menu: null,
    open: null,
    close: null
  })
  const onAction = useCallbackRef(_onAction)

  React.useEffect(() => {
    if (activeItemIndex === activeItemIndexRef.current) return

    activeItemIndexRef.current = activeItemIndex
  }, [activeItemIndex])

  React.useEffect(() => {
    if (!isSelectable) return
    if (selectedItemIndex === selectedItemIndexRef.current) return

    selectedItemIndexRef.current = selectedItemIndex
  }, [isSelectable, selectedItemIndex])

  const checkOrientationRestrictions = React.useCallback(
    key => {
      const isVertical = orientation === 'vertical'
      const isHorizontal = orientation === 'horizontal'
      const isUpArrow = key === KeyboardKey.UpArrow
      const isDownArrow = key === KeyboardKey.DownArrow
      const isLeftArrow = key === KeyboardKey.LeftArrow
      const isRightArrow = key === KeyboardKey.RightArrow

      return (
        (isHorizontal && (isUpArrow || isDownArrow)) ||
        (isVertical && (isLeftArrow || isRightArrow))
      )
    },
    [orientation]
  )

  const focusTarget = React.useCallback(
    key => {
      if (!key) return
      const target = key === 'trigger' ? triggerRef : menuRef
      const isHover = trigger === TRIGGER.hover

      timeoutIds.current[key] = setTimeout(
        () => {
          target.current?.focus()
        },
        isHover ? HOVER_TRANSITION_DURATION : CLICK_TRANSITION_DURATION
      )
    },
    [trigger]
  )

  const hideDropdownMenu = React.useCallback(
    event => {
      if (!isOpen) return

      setIsOpen(false)
      activeItemIndexRef.current = isSelectable
        ? selectedItemIndexRef.current || -1
        : -1

      if (!returnFocusOnClose && finalFocusRef?.current) {
        if (
          trigger === TRIGGER.click ||
          (trigger === TRIGGER.hover && event?.type === 'keydown')
        ) {
          finalFocusRef.current.focus()
          return
        }
      }

      if (
        returnFocusOnClose &&
        !finalFocusRef?.current &&
        (trigger === TRIGGER.click || isMobile())
      )
        focusTarget('trigger')
    },
    [
      isOpen,
      isSelectable,
      returnFocusOnClose,
      finalFocusRef,
      trigger,
      focusTarget
    ]
  )

  const showDropdownMenu = React.useCallback(
    (event, options = { focusIndex: 0 }) => {
      preventBubblingEvent(event)
      if (disabled) return

      if (isOpen) {
        hideDropdownMenu(event)
        return
      }

      if (activeItemIndexRef.current === -1)
        activeItemIndexRef.current = options.focusIndex

      focusTarget('menu')

      setIsOpen(true)
    },
    [disabled, isOpen, focusTarget, hideDropdownMenu]
  )

  const outSideEventHandler = React.useCallback(
    event => {
      if (inline || !isOpen || triggerRef.current?.contains(event.target))
        return

      hideDropdownMenu(event)
    },
    [inline, isOpen, hideDropdownMenu]
  )

  const handleOnItemClick = React.useCallback(
    (event, key) => {
      if (disabled) return

      if (isSelectable) {
        selectedItemIndexRef.current = activeItemIndexRef.current
      }

      onAction(key, event, activeItemIndexRef.current)
      if (closeOnSelect) {
        hideDropdownMenu(event)
      }
    },
    [disabled, isSelectable, onAction, hideDropdownMenu, closeOnSelect]
  )

  const handleOnItemKeyDown = React.useCallback(
    event => {
      if (disabled) return

      if (event.shiftKey && event.keyCode === KeyboardKey.Tab) {
        preventBubblingEvent(event)
        hideDropdownMenu(event)
        return
      }

      const items = menuItems.current
      let index = 0
      let changedIndex = false

      const currentIndex = items.indexOf(document.activeElement)

      if (currentIndex === -1) return

      switch (event.keyCode) {
        case KeyboardKey.Escape:
          preventBubblingEvent(event)
          hideDropdownMenu(event)
          return
        case KeyboardKey.Tab:
          setIsOpen(false)
          activeItemIndexRef.current = isSelectable
            ? selectedItemIndexRef.current || -1
            : -1
          return
        case KeyboardKey.Space:
        case KeyboardKey.Enter:
          preventBubblingEvent(event)
          items[currentIndex].click()
          return
        case KeyboardKey.LeftArrow:
        case KeyboardKey.UpArrow:
          if (checkOrientationRestrictions(event.keyCode)) return

          preventBubblingEvent(event)
          index = Math.max(0, currentIndex - 1)
          changedIndex = true
          break
        case KeyboardKey.RightArrow:
        case KeyboardKey.DownArrow:
          if (checkOrientationRestrictions(event.keyCode)) return

          preventBubblingEvent(event)
          index = Math.min(items.length - 1, currentIndex + 1)
          changedIndex = true
          break
        case KeyboardKey.Home:
          preventBubblingEvent(event)
          index = 0
          changedIndex = true
          break
        case KeyboardKey.End:
          preventBubblingEvent(event)
          index = items.length - 1
          changedIndex = true
          break
        default:
          break
      }

      if (changedIndex && index !== currentIndex) {
        items[index].focus()
        activeItemIndexRef.current = index
      }
    },
    [disabled, hideDropdownMenu, isSelectable, checkOrientationRestrictions]
  )

  const handleOnTriggerKeyDown = React.useCallback(
    event => {
      if (disabled) return

      switch (event.keyCode) {
        case KeyboardKey.Escape:
          preventBubblingEvent(event)

          if (!isOpen || inline) return
          hideDropdownMenu(event)
          break
        case KeyboardKey.Enter:
        case KeyboardKey.Space:
        case KeyboardKey.DownArrow:
          preventBubblingEvent(event)

          showDropdownMenu(event, { focusIndex: 0 })
          break
        case KeyboardKey.UpArrow:
          preventBubblingEvent(event)

          showDropdownMenu(event, { focusIndex: menuItems.current.length - 1 })
          break
        default:
          break
      }
    },
    [disabled, isOpen, inline, showDropdownMenu, hideDropdownMenu]
  )

  const handleOnMenuKeyDown = React.useCallback(
    event => {
      if (disabled || !isOpen) return

      switch (event.keyCode) {
        case KeyboardKey.Escape:
          preventBubblingEvent(event)
          hideDropdownMenu(event)
          break
        case KeyboardKey.LeftArrow:
        case KeyboardKey.UpArrow:
          if (checkOrientationRestrictions(event.keyCode)) return
          preventBubblingEvent(event)
          focusItem(
            Math.min(
              menuItems.current.length - 1,
              activeItemIndexRef.current + 1
            )
          )
          break
        case KeyboardKey.RightArrow:
        case KeyboardKey.DownArrow:
          if (checkOrientationRestrictions(event.keyCode)) return
          preventBubblingEvent(event)
          focusItem(Math.max(0, activeItemIndexRef.current - 1))
          break
        default:
          break
      }
    },
    [disabled, isOpen, inline, hideDropdownMenu]
  )

  const handleOnSpaceKeyUp = React.useCallback(
    event => {
      if (disabled) return

      switch (event.keyCode) {
        case KeyboardKey.Space:
          event.preventDefault()
          break
        default:
          break
      }
    },
    [disabled]
  )

  const handleOnItemMouseOver = React.useCallback(
    event => {
      const currentIndex = menuItems.current.indexOf(event.currentTarget)

      if (currentIndex !== -1) {
        activeItemIndexRef.current = currentIndex
      }

      if (disabled || !focusItemOnHover) return
      if (currentIndex === -1) return

      event.currentTarget?.focus()
    },
    [focusItemOnHover, disabled]
  )

  function focusItem(index) {
    if (!index && activeItemIndexRef.current === -1) return
    menuItems.current[index ?? activeItemIndexRef.current].focus()
  }

  const handleMenuFocus = React.useCallback(() => {
    if (!focusItemOnOpen) return
    if (menuItems.current.length === 0) return
    focusItem()
  }, [focusItemOnOpen])

  const handleMenuBlur = React.useCallback(
    event => {
      if (!closeOnBlur) return

      const relatedTarget = getRelatedTarget(event)
      const targetIsMenu = contains(menuRef.current, relatedTarget)
      const targetIsTrigger = contains(triggerRef.current, relatedTarget)
      const isValidBlur = !targetIsMenu && !targetIsTrigger

      if (isOpen && isValidBlur) {
        hideDropdownMenu(event)
      }
    },
    [inline, isOpen, hideDropdownMenu, closeOnBlur]
  )

  const _children = useMemo(
    () =>
      runIfFn(childrenProp, {
        isOpen,
        onClose: hideDropdownMenu,
        onOpen: showDropdownMenu
      }),
    [childrenProp, isOpen, hideDropdownMenu, showDropdownMenu]
  )
  useEventListener('focus', handleMenuFocus, menuRef)
  useOnScrollOutside(menuRef, outSideEventHandler)

  React.useEffect(() => {
    if (disabled) return
    if (!menuRef.current) return

    menuItems.current = Array.from(
      menuRef.current.querySelectorAll('[role="menuitem"]')
    )

    if (!isSelectable) activeItemIndexRef.current = 0

    return () => {
      Object.values(timeoutIds.current).forEach(id => {
        clearTimeout(id)
      })

      timeoutIds.current = {}
    }
  }, [childrenProp, disabled])

  const childDefaultProps = React.useMemo(
    () => ({
      inline,
      variant,
      role: 'menuitem',
      tabIndex: -1,
      onKeyDown: handleOnItemKeyDown,
      onKeyUp: handleOnSpaceKeyUp,
      onMouseOver: handleOnItemMouseOver
    }),
    [
      inline,
      variant,
      handleOnItemKeyDown,
      handleOnItemMouseOver,
      handleOnSpaceKeyUp
    ]
  )

  const children = React.useMemo(
    () =>
      React.Children.map(_children, child => {
        if (!React.isValidElement(child)) {
          if (child !== null)
            console.error('[useDropdown] Invalid elements detected', { child })
          return child
        }

        const isDivider =
          child.type.defaultProps?.internalId ===
          DropdownDivider.defaultProps.internalId

        const isHeader =
          child.type.defaultProps?.internalId ===
          DropdownHeader.defaultProps.internalId

        if ((!isDivider || !isHeader) && !child.key) {
          console.error('[useDropdown] All children must have a unique key', {
            child
          })
          return child
        }

        const childProps = {
          ...childDefaultProps,
          ...child.props,
          className: mergeClassnames(
            !(isDivider || isHeader) && 'dropdown-item',
            isDivider && 'dropdown-divider',
            isHeader && 'dropdown-header',
            child.props.className
          ),
          onClick: event => handleOnItemClick(event, child.key)
        }

        if (isDivider || isHeader) {
          childProps.role = 'presentation'

          Array.from([
            'onClick',
            'onKeyDown',
            'onKeyUp',
            'onMouseOver'
          ]).forEach(prop => {
            if (prop in childProps) delete childProps[prop]
          })
        }

        return React.cloneElement(child, childProps)
      }).filter(Boolean),
    [_children, childDefaultProps, handleOnItemClick]
  )

  const triggerProps = React.useMemo(() => {
    const props = {
      disabled,
      ref: triggerRef,
      className: mergeClassnames(
        'dropdown-trigger-container',
        triggerClassName
      ),
      type: 'button',
      'data-testid': triggerTestId,
      'aria-label': ariaLabel,
      'aria-disabled': disabled,
      'aria-expanded': isOpen,
      'aria-haspopup': true,
      'aria-controls': ariaControls ?? id
    }

    if (trigger === TRIGGER.click || isMobile()) {
      props.onClick = showDropdownMenu
      props.onKeyDown = handleOnTriggerKeyDown
      props.onKeyUp = handleOnSpaceKeyUp
    }

    if (trigger === TRIGGER.hover && !isMobile()) {
      props.onFocus = showDropdownMenu
      props.onBlur = event => {
        const relatedTarget = getRelatedTarget(event)
        const isValidBlur = !contains(menuRef.current, relatedTarget)

        if (isOpen && isValidBlur) {
          hideDropdownMenu(event)
        }
      }
      props.onMouseEnter = event => {
        isHoveringRef.current = true
        timeoutIds.current.open = setTimeout(
          () => showDropdownMenu(event),
          HOVER_DELAY_DURATION
        )
      }
      props.onMouseLeave = event => {
        isHoveringRef.current = false

        if (timeoutIds.current.open) {
          clearTimeout(timeoutIds.current.open)
          timeoutIds.current.open = null
        }

        timeoutIds.current.close = setTimeout(() => {
          if (isHoveringRef.current === false) {
            hideDropdownMenu(event)
          }
        }, HOVER_DELAY_DURATION)
      }
    }

    return props
  }, [
    trigger,
    triggerClassName,
    triggerTestId,
    ariaLabel,
    disabled,
    isOpen,
    ariaControls,
    id,
    hideDropdownMenu,
    showDropdownMenu,
    handleOnTriggerKeyDown,
    handleOnSpaceKeyUp
  ])

  const menuProps = React.useMemo(() => {
    const props = {
      id,
      inline,
      width,
      disabled,
      placement,
      isOpen,
      orientation,
      variant,
      transitionType,
      className: mergeClassnames('dropdown-container', menuClassName),
      ref: menuRef,
      ...rest,
      onKeyDown: handleOnMenuKeyDown,
      onBlur: handleMenuBlur,
      tabIndex: -1,
      'data-testid': id,
      role: 'menu',
      'aria-hidden': !isOpen,
      'aria-labelledby': !ariaLabel ? ariaLabelledBy : null,
      'aria-orientation': orientation
    }

    if (trigger === TRIGGER.hover && !isMobile()) {
      props.onMouseEnter = () => {
        isHoveringRef.current = true
      }

      props.onMouseLeave = event => {
        isHoveringRef.current = false
        timeoutIds.current.close = setTimeout(
          () => hideDropdownMenu(event),
          HOVER_DELAY_DURATION
        )
      }
    }

    return props
  }, [
    transitionType,
    id,
    inline,
    menuClassName,
    width,
    disabled,
    placement,
    isOpen,
    orientation,
    variant,
    rest,
    ariaLabel,
    ariaLabelledBy,
    trigger,
    hideDropdownMenu,
    handleOnMenuKeyDown,
    handleMenuBlur
  ])

  return {
    menuProps,
    triggerProps,
    children,
    isOpen,
    trigger,
    showDropdownMenu,
    hideDropdownMenu
  }
}
